import React, { useState, useEffect, useCallback, useRef } from 'react';
import WebGPUCanvas from './components/WebGPUCanvas';
import Controls from './components/Controls';
import { Renderer } from './renderer/Renderer';
import { RenderMode } from './renderer/types';
import { pipeline, env } from '@xenova/transformers';
import './style.css';

env.allowLocalModels = false;
// env.backends.onnx.executionProviders = ['webgpu'];
env.backends.onnx.logLevel = 'warning';
const model_loc = 'Xenova/dpt-hybrid-midas'

function App() {
  const [mode, setMode] = useState<RenderMode>('liquid');
  const [zoom, setZoom] = useState(1.0);
  const [panX, setPanX] = useState(0.5);
  const [panY, setPanY] = useState(0.5);
  const [autoChangeEnabled, setAutoChangeEnabled] = useState(false);
  const [autoChangeDelay, setAutoChangeDelay] = useState(10);
  const [status, setStatus] = useState('Ready. Click "Load AI Model" for depth effects.');
  const [depthEstimator, setDepthEstimator] = useState<any>(null);
  const [depthMapResult, setDepthMapResult] = useState<any>(null);
  const [farthestPoint, setFarthestPoint] = useState({ x: 0.5, y: 0.5 });
  const [mousePosition, setMousePosition] = useState({ x: -1, y: -1 });
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [allShaders, setAllShaders] = useState<string[]>([]);
  const [shaderVariations, setShaderVariations] = useState<string[]>([]);
  const [selectedShader, setSelectedShader] = useState<string>('');
  const [isRendererInitialized, setIsRendererInitialized] = useState(false);

  const rendererRef = useRef<Renderer | null>(null);
  const debugCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    // This effect hook now correctly handles the filtering of shader variations
    // based on the current render mode. It ensures that when the mode changes,
    // the list of available shaders is updated, and a sensible default is chosen.

    // Filter the shaders that are relevant to the current render mode.
    const variations = allShaders.filter(shader => shader.startsWith(mode));
    setShaderVariations(variations);

    // If there are variations, select a default. Prioritize the exact match
    // (e.g., 'liquid.wgsl' for 'liquid' mode), otherwise, pick the first
    // available variation. This prevents the dropdown from being empty or
    // showing an unrelated shader.
    if (variations.length > 0) {
        const defaultShader = variations.find(v => v === `${mode}.wgsl`) || variations[0];
        setSelectedShader(defaultShader);
    } else {
        // If there are no variations for the current mode, clear the selection.
        setSelectedShader('');
    }
  }, [mode, allShaders]); // Dependencies: runs when mode or the list of all shaders changes

  useEffect(() => {
    const fetchShaders = async () => {
        if (isRendererInitialized && rendererRef.current) {
            const shaders = await rendererRef.current.fetchShaderFiles();
            setAllShaders(shaders);
        }
    };
    fetchShaders();
  }, [isRendererInitialized]);

  const loadModel = async () => {
        if (depthEstimator) { setStatus('Model already loaded.'); return; }
        try {
            setStatus('Loading model...');
            const estimator = await pipeline('depth-estimation', model_loc, {
                 progress_callback: (progress: any) => {
                    if (progress.status === 'progress' && typeof progress.progress === 'number') {
                        setStatus(`Loading model... ${progress.progress.toFixed(2)}%`);
                    } else {
                        setStatus(progress.status);
                    }
                },
            quantized: false // Correct: Use this to load the FP32 model
            });
            setDepthEstimator(() => estimator);
            setStatus('Model Loaded. Processing initial image...');
        } catch (e: any) {
            console.error(e);
            setStatus(`Failed to load model: ${e.message}`);
        }
    };

  const runDepthAnalysis = useCallback(async (imageUrl: string) => {
      if (!depthEstimator || !rendererRef.current) return;
      setStatus('Analyzing image with AI model...');
      try {
          const result = await depthEstimator(imageUrl);
          const { data, dims } = result.predicted_depth;
          const [height, width] = [dims[dims.length - 2], dims[dims.length - 1]];

          let min = Infinity, max = -Infinity;
          let minIndex = 0;
          data.forEach((v: number, i: number) => {
              if (v < min) {
                  min = v;
                  minIndex = i;
              }
              if (v > max) max = v;
          });

          const farthestY = Math.floor(minIndex / width);
          const farthestX = minIndex % width;
          setFarthestPoint({ x: farthestX / width, y: farthestY / height });

          const range = max - min;
          const normalizedData = new Float32Array(data.length);

          for (let i = 0; i < data.length; ++i) {
              normalizedData[i] = 1.0 - ((data[i] - min) / range);
          }

          setStatus('Updating depth map on GPU...');
          rendererRef.current.updateDepthMap(normalizedData, width, height);

          setDepthMapResult(result);
          setStatus('Ready.');
      } catch (e: any) {
          console.error("Error during analysis:", e);
          setStatus(`Failed to analyze image: ${e.message}`);
      }
  }, [depthEstimator]);

  const handleNewImage = useCallback(async () => {
      if (!rendererRef.current) {
          console.warn("Renderer not ready yet.");
          return;
      }
      setStatus('Loading random image...');
      const newImageUrl = await rendererRef.current.loadRandomImage();

      if (newImageUrl) {
          if (depthEstimator) {
              await runDepthAnalysis(newImageUrl);
          } else {
              setFarthestPoint({ x: 0.5, y: 0.5 });
              setStatus('Ready. Load AI model to add depth effects.');
          }
      } else {
          setStatus('Failed to load a random image.');
      }
  }, [depthEstimator, runDepthAnalysis]);

  useEffect(() => {
      let intervalId: NodeJS.Timeout | null = null;
      if (autoChangeEnabled) {
          intervalId = setInterval(handleNewImage, autoChangeDelay * 1000);
      }
      return () => { if (intervalId) clearInterval(intervalId); };
  }, [autoChangeEnabled, autoChangeDelay, handleNewImage]);

  useEffect(() => {
      if (depthMapResult?.predicted_depth && debugCanvasRef.current) {
          const { data, dims } = depthMapResult.predicted_depth;
          const [height, width] = [dims[dims.length - 2], dims[dims.length - 1]];
          const canvas = debugCanvasRef.current;
          const context = canvas.getContext('2d');
          if (!width || !height || !context) return;

          canvas.width = width;
          canvas.height = height;
          const imageData = context.createImageData(width, height);

          let min = Infinity, max = -Infinity;
          data.forEach((v: number) => {
              if (v < min) min = v;
              if (v > max) max = v;
          });
          const range = max - min;
          for (let i = 0; i < data.length; ++i) {
              const value = Math.round(((data[i] - min) / range) * 255);
              imageData.data[i * 4 + 0] = value;
              imageData.data[i * 4 + 1] = value;
              imageData.data[i * 4 + 2] = value;
              imageData.data[i * 4 + 3] = 255;
          }
          context.putImageData(imageData, 0, 0);
      }
  }, [depthMapResult]);

  return (
    <div id="app-container">
        <h1>WebGPU Liquid + Depth Effect</h1>
        <p><strong>Status:</strong> {status}</p>
        <Controls
            mode={mode}
            setMode={setMode}
            zoom={zoom} setZoom={setZoom}
            panX={panX} setPanX={setPanX}
            panY={panY} setPanY={setPanY}
            onNewImage={handleNewImage}
            autoChangeEnabled={autoChangeEnabled}
            setAutoChangeEnabled={setAutoChangeEnabled}
            autoChangeDelay={autoChangeDelay}
            setAutoChangeDelay={setAutoChangeDelay}
            onLoadModel={loadModel}
            isModelLoaded={!!depthEstimator}
            shaderVariations={shaderVariations}
            selectedShader={selectedShader}
            setSelectedShader={setSelectedShader}
        />
        <WebGPUCanvas
            rendererRef={rendererRef}
            mode={mode}
            selectedShader={selectedShader}
            zoom={zoom}
            panX={panX}
            panY={panY}
            farthestPoint={farthestPoint}
            mousePosition={mousePosition}
            setMousePosition={setMousePosition}
            isMouseDown={isMouseDown}
            setIsMouseDown={setIsMouseDown}
            onRendererInitialized={() => setIsRendererInitialized(true)}
        />
        {depthMapResult && (
            <div className="debug-container">
                <h2>AI Model Output (Debug Depth Map)</h2>
                <canvas ref={debugCanvasRef} style={{ maxWidth: '100%', height: 'auto', border: '1px solid grey' }} />
            </div>
        )}
    </div>
);
}

export default App;
